/**
 * PIN 관리 컨텍스트
 * 마스터 PIN과 PIN 보호 설정을 관리
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  setMasterPin,
  verifyMasterPin,
  changeMasterPin,
  removeMasterPin,
  hasMasterPin,
  getPinSettings,
  updatePinSettings,
  verifyProtection,
  migrateIndividualPins
} from '../services/unifiedPinService';

const PinContext = createContext();

export function usePin() {
  const context = useContext(PinContext);
  if (!context) {
    throw new Error('usePin must be used within PinProvider');
  }
  return context;
}

export function PinProvider({ children }) {
  const { user } = useAuth();
  const [pinSettings, setPinSettings] = useState({
    hasMasterPin: false,
    autoLockMinutes: 5
  });
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [unlockExpiresAt, setUnlockExpiresAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // PIN 설정 로드
  useEffect(() => {
    if (user) {
      loadPinSettings();
    } else {
      setPinSettings({
        hasMasterPin: false,
        autoLockMinutes: 5
      });
      setIsUnlocked(false);
    }
  }, [user]);

  // 자동 잠금 타이머
  useEffect(() => {
    if (isUnlocked && unlockExpiresAt) {
      const timer = setTimeout(() => {
        if (Date.now() >= unlockExpiresAt) {
          setIsUnlocked(false);
          setUnlockExpiresAt(null);
        }
      }, unlockExpiresAt - Date.now());
      
      return () => clearTimeout(timer);
    }
  }, [isUnlocked, unlockExpiresAt]);

  // PIN 설정 로드
  const loadPinSettings = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const settings = await getPinSettings(user.uid);
      setPinSettings(settings);
    } catch (err) {
      console.error('Error loading PIN settings:', err);
      setError('PIN 설정을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 마스터 PIN 설정
  const setMasterPinForUser = async (pin) => {
    if (!user) throw new Error('User not authenticated');
    
    setLoading(true);
    setError(null);
    
    try {
      await setMasterPin(user.uid, pin);
      setPinSettings(prev => ({ ...prev, hasMasterPin: true }));
      setIsUnlocked(true);
      updateUnlockExpiry();
      return true;
    } catch (err) {
      console.error('Error setting master PIN:', err);
      setError('마스터 PIN 설정에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 마스터 PIN 검증
  const verifyMasterPinForUser = async (pin) => {
    if (!user) throw new Error('User not authenticated');
    
    setLoading(true);
    setError(null);
    
    try {
      const isValid = await verifyMasterPin(user.uid, pin);
      if (isValid) {
        setIsUnlocked(true);
        updateUnlockExpiry();
      }
      return isValid;
    } catch (err) {
      console.error('Error verifying master PIN:', err);
      setError('PIN 검증에 실패했습니다.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 마스터 PIN 변경
  const changeMasterPinForUser = async (oldPin, newPin) => {
    if (!user) throw new Error('User not authenticated');
    
    setLoading(true);
    setError(null);
    
    try {
      await changeMasterPin(user.uid, oldPin, newPin);
      return true;
    } catch (err) {
      console.error('Error changing master PIN:', err);
      setError(err.message || '마스터 PIN 변경에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 마스터 PIN 제거
  const removeMasterPinForUser = async (currentPin) => {
    if (!user) throw new Error('User not authenticated');
    
    setLoading(true);
    setError(null);
    
    try {
      await removeMasterPin(user.uid, currentPin);
      setPinSettings(prev => ({ ...prev, hasMasterPin: false }));
      setIsUnlocked(false);
      setUnlockExpiresAt(null);
      return true;
    } catch (err) {
      console.error('Error removing master PIN:', err);
      setError(err.message || '마스터 PIN 제거에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // PIN 설정 업데이트
  const updatePinSettingsForUser = async (newSettings) => {
    if (!user) throw new Error('User not authenticated');
    
    setLoading(true);
    setError(null);
    
    try {
      await updatePinSettings(user.uid, newSettings);
      setPinSettings(prev => ({ ...prev, ...newSettings }));
      
      // 자동 잠금 시간이 변경되면 만료 시간 업데이트
      if (newSettings.autoLockMinutes !== undefined && isUnlocked) {
        updateUnlockExpiry(newSettings.autoLockMinutes);
      }
      
      return true;
    } catch (err) {
      console.error('Error updating PIN settings:', err);
      setError('PIN 설정 업데이트에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 통합 PIN 검증 (Master PIN만 사용)
  const verifyPin = async (pin) => {
    if (!user) throw new Error('User not authenticated');

    try {
      const isValid = await verifyProtection(user.uid, pin);
      if (isValid) {
        setIsUnlocked(true);
        updateUnlockExpiry();
      }
      return isValid;
    } catch (err) {
      console.error('Error verifying PIN:', err);
      return false;
    }
  };

  // 잠금 해제 만료 시간 업데이트
  const updateUnlockExpiry = (minutes = null) => {
    const autoLockMinutes = minutes || pinSettings.autoLockMinutes || 5;
    if (autoLockMinutes === -1) {
      setUnlockExpiresAt(null); // 자동 잠금 없음
    } else {
      setUnlockExpiresAt(Date.now() + (autoLockMinutes * 60 * 1000));
    }
  };

  // 수동 잠금
  const lock = () => {
    setIsUnlocked(false);
    setUnlockExpiresAt(null);
  };

  // 잠금 해제 상태 확인
  const checkUnlockStatus = useCallback(() => {
    if (!pinSettings.hasMasterPin) return true; // PIN이 없으면 항상 열림
    if (!isUnlocked) return false;
    if (!unlockExpiresAt) return true;
    
    if (Date.now() >= unlockExpiresAt) {
      setIsUnlocked(false);
      setUnlockExpiresAt(null);
      return false;
    }
    
    return true;
  }, [isUnlocked, unlockExpiresAt, pinSettings.hasMasterPin]);

  const value = {
    pinSettings,
    isUnlocked,
    loading,
    error,
    setMasterPin: setMasterPinForUser,
    verifyMasterPin: verifyMasterPinForUser,
    changeMasterPin: changeMasterPinForUser,
    removeMasterPin: removeMasterPinForUser,
    updatePinSettings: updatePinSettingsForUser,
    verifyPin, // 통합된 검증 함수
    lock,
    checkUnlockStatus,
    loadPinSettings
  };

  return (
    <PinContext.Provider value={value}>
      {children}
    </PinContext.Provider>
  );
}

export default PinContext;